import React, { useEffect, memo, useState, useMemo } from 'react'

import { Form, Formik } from 'formik'

import {
    Avatar,
    Box,
    Button,
    Chip,
    makeStyles,
    TextField,
    Typography
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import CloseIcon from '@material-ui/icons/Close'
import LinkIcon from '@material-ui/icons/Link'
import GifIcon from '@material-ui/icons/Gif'

import { StartAtInput } from './StartAtInput'
import {
    copyToClipboard,
    copyLinkedGif,
    generateStartValue,
    timeToSeconds,
    option
} from './utils'

import { useBoolean } from '@guidde-co/shared.hooks.use-boolean'

import { PlaybookType, QuickGuiddeType } from './types'

import { SpacedGroup } from '@guidde-co/shared.spaced-group'
import { WhiteButton } from '@guidde-co/shared.white-button'

const useStyles = makeStyles({
    disabled: {
        backgroundColor: '#CB0000 !important',
        opacity: 0.4,
        color: '#FFFFFF !important'
    },
    endAdornment: {
        top: '-16px !important',
        right: '8px'
    },
    greyLine: {
        position: 'absolute',
        bottom: '-1px',
        left: 0,
        right: 0,
        background: '#fff',
        height: '5px',
        width: '100%'
    },
    redLine: {
        position: 'absolute',
        bottom: '-4px',
        left: 0,
        right: 0,
        background: '#cb0000',
        height: '1px',
        width: 'calc(100% + 20px)',
        transform: 'translateX(-10px)'
    },
    chip: {
        color: '#090C10',
        fontSize: 14,
        background: 'rgba(9, 12, 16, 0.08)',
        border: '1px solid rgba(9, 12, 16, 0.08)',
        borderRadius: 16,
        height: '24px !important'
    }
})

export type ShareableSpacesUserType = {
    displayName: string
    email: string
    isSelected: boolean
    logo: string
    uid: string
}

export type PendingEmailType = {
    email: string
    isSelected: boolean
}

export type OptionsType = {
    spaces: Array<{
        id: string
        name: string
        logo: string
        isSelected: boolean
        isSuggested?: boolean
        isDisabled: boolean
    }>
    internalUsers: Array<ShareableSpacesUserType>
    externalUsers: Array<ShareableSpacesUserType>
    pendingEmails: Array<PendingEmailType>
}

type Props = {
    options?: OptionsType
    playbook: PlaybookType
    origin?: string
    onClose: () => void
    hideCopyLink?: boolean
    isLoading: boolean
    currentTime: number
    steps: Array<QuickGuiddeType>
    onShare: ({
        emails,
        userIds
    }: {
        emails: Array<string>
        userIds: Array<string>
    }) => void
    copyLinkPath: string
    uid: string
    onCopySharedLink: () => void
    onCopyGifLink: () => void
}

type SubmitType = {
    options: Array<{
        isSelected: boolean
        label: string
        value: string
        email: string
        name: string
        logo: string
        group: typeof GROUP.pendingEmails | typeof GROUP.teamMembers
    }>
}

export const GROUP = {
    spaces: 'Spaces',
    teamMembers: 'Team Members',
    externalMembers: 'External Members',
    pendingEmails: 'Pending Emails'
}

export const getEmailOption = (user: PendingEmailType) => ({
    ...option(user.email, user.email),
    isSelected: user.isSelected,
    group: GROUP.pendingEmails,
    email: user.email,
    name: '',
    logo: ''
})

export const getUserOption = (user: ShareableSpacesUserType) => ({
    ...option(user.uid, user.email),
    isSelected: user.isSelected,
    group: GROUP.teamMembers,
    email: user.email,
    name: user.displayName,
    logo: user.logo
})

export const ShareTab = memo(
    ({
        currentTime,
        options,
        playbook,
        hideCopyLink,
        steps = [],
        origin = window.location.origin,
        onShare,
        isLoading,
        copyLinkPath,
        uid,
        onCopySharedLink,
        onCopyGifLink
    }: Props) => {
        const classes = useStyles()

        const startAt = useBoolean()
        const focussed = useBoolean()

        const [startTime, setStartTime] = useState('00:00:00')

        useEffect(() => {
            setStartTime(generateStartValue(currentTime))
        }, [currentTime])

        const isQG = playbook.mode === 'quickGuidde'

        const optionsList = useMemo(
            () => [
                ...(options?.internalUsers?.map(getUserOption) || []),
                ...(options?.externalUsers?.map(getUserOption) || []),
                ...(options?.pendingEmails?.map(getEmailOption) || [])
            ],
            [options]
        )

        return (
            <Box p={2}>
                <StartAtInput
                    startAt={startAt}
                    startTime={startTime}
                    setStartTime={setStartTime}
                />

                <SpacedGroup width="100%" mt={2} spacing={3}>
                    {!hideCopyLink && (
                        <WhiteButton
                            startIcon={<LinkIcon />}
                            fullWidth
                            color="primary"
                            onClick={() => {
                                copyToClipboard(
                                    `${origin}${copyLinkPath}?origin=${uid}${
                                        startAt.isTrue
                                            ? `&t=${timeToSeconds(startTime)}`
                                            : ''
                                    }`
                                ).then(onCopySharedLink)
                            }}
                        >
                            COPY LINK
                        </WhiteButton>
                    )}
                    <WhiteButton
                        startIcon={<GifIcon />}
                        style={{ width: hideCopyLink ? '25%' : '100%' }}
                        color="default"
                        onClick={() => {
                            copyLinkedGif({
                                copyLinkPath,
                                origin,
                                uid,
                                playbook,
                                isQuickGuidde: playbook.mode === 'quickGuidde'
                            }).then(onCopyGifLink)
                        }}
                    >
                        COPY GIF
                    </WhiteButton>
                </SpacedGroup>
                <Box mt={4} />
                <Formik
                    initialValues={{
                        options: optionsList.filter(it => it.isSelected)
                    }}
                    enableReinitialize={true}
                    onSubmit={({ options }: SubmitType) => {
                        const filteredEmails = options.filter(
                            ({ group }) => group === GROUP.pendingEmails
                        )
                        // we have empty list is user is Anonymous
                        const emails =
                            optionsList.length > 0 ? filteredEmails : options

                        onShare({
                            emails: emails.map(o => o.value),
                            userIds: options
                                .filter(
                                    ({ group }) => group === GROUP.teamMembers
                                )
                                .map(o => o.value)
                        })
                    }}
                >
                    {({ dirty, values, setFieldValue }) => (
                        <Form>
                            <Autocomplete
                                multiple
                                value={values.options}
                                onChange={(e, value) => {
                                    if (
                                        value.every(
                                            it => typeof it === 'object'
                                        )
                                    ) {
                                        setFieldValue('options', value)
                                    } else {
                                        const modifiedValue = value.map(it => {
                                            if (typeof it === 'object')
                                                return it

                                            return {
                                                value: it,
                                                label: it,
                                                isSelected: false,
                                                group: GROUP.pendingEmails
                                            }
                                        })
                                        setFieldValue('options', modifiedValue)
                                    }
                                }}
                                onFocus={focussed.setTrue}
                                onBlur={focussed.setFalse}
                                freeSolo
                                options={optionsList}
                                getOptionLabel={option => option.label}
                                filterSelectedOptions
                                style={{
                                    background: '#FFFFFF',
                                    padding: `${
                                        values.options.length < 1 ? 0 : 8
                                    }px 8px 4px`,
                                    marginTop:
                                        values.options.length < 1 ? '-10px' : 0,
                                    borderRadius: '4px 4px 0 0',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    maxHeight: '200px'
                                }}
                                classes={{
                                    endAdornment: classes.endAdornment
                                }}
                                renderTags={(params, getProps) => {
                                    return params.map((it, index) => (
                                        <Chip
                                            label={
                                                it.name || it.email || it.value
                                            }
                                            key={it.value}
                                            classes={{ root: classes.chip }}
                                            deleteIcon={
                                                <CloseIcon
                                                    fontSize="small"
                                                    style={{
                                                        width: 15,
                                                        height: 15,
                                                        color: '#090C10',
                                                        marginRight: 5
                                                    }}
                                                />
                                            }
                                            {...getProps({ index })}
                                        />
                                    ))
                                }}
                                renderOption={params => (
                                    <SpacedGroup>
                                        <Avatar
                                            src={params.logo}
                                            style={{ width: 40, height: 40 }}
                                        />
                                        <Box>
                                            <Typography
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 500
                                                }}
                                            >
                                                {params.name}
                                            </Typography>
                                            <Typography
                                                color="textSecondary"
                                                style={{ fontSize: 14 }}
                                            >
                                                {params.email}
                                            </Typography>
                                        </Box>
                                    </SpacedGroup>
                                )}
                                renderInput={params => (
                                    <Box position="relative">
                                        <TextField
                                            {...params}
                                            style={{
                                                textAlign: 'center',
                                                color: 'rgba(0, 0, 0, 0.54)  !important'
                                            }}
                                            label={
                                                <Box
                                                    color={
                                                        focussed.isTrue
                                                            ? 'inherit'
                                                            : 'rgba(0, 0, 0, 0.54) !important'
                                                    }
                                                    mt="-3px"
                                                    fontSize={
                                                        focussed.isTrue
                                                            ? 'inherit'
                                                            : '14px'
                                                    }
                                                >
                                                    Send email
                                                </Box>
                                            }
                                        />
                                        <Box className={classes.greyLine} />
                                        {focussed.isTrue && (
                                            <Box className={classes.redLine} />
                                        )}
                                    </Box>
                                )}
                            />
                            <Box
                                mt={2}
                                display="flex"
                                justifyContent="flex-end"
                                width="100%"
                            >
                                <Button
                                    type="submit"
                                    disabled={!dirty || isLoading}
                                    color="primary"
                                    className={
                                        !dirty || isLoading
                                            ? classes.disabled
                                            : ''
                                    }
                                >
                                    SEND
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        )
    }
)
